import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CodexLockup } from "@/components/brand/CodexLockup";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast("Welcome back, explorer");
      } else {
        await register(name, email, password);
        toast("Your archive account is ready");
      }
      // Return the user to where they came from (e.g. the Archivist with their
      // preserved question) when a safe internal ?next= path is present.
      navigate(next && next.startsWith("/") ? next : "/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast(typeof msg === "string" ? msg : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-16">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(700px 400px at 50% 0%, rgba(216,182,106,0.10), rgba(216,182,106,0) 60%)" }} />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative w-full max-w-md">
        <Link to="/" aria-label="CODEX — Archive of the Unexplained" data-testid="auth-brand-lockup" className="mb-8 flex justify-center">
          <CodexLockup vertical iconSize={76} wordmarkImage wordmarkImgClassName="w-[168px]" showSubtitle />
        </Link>

        <div className="codex-glass-strong rounded-2xl p-6">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-full border border-white/10 p-1">
            <button onClick={() => setMode("login")} className={`rounded-full py-2 text-sm font-medium transition-colors ${mode === "login" ? "bg-gold-light text-[color:var(--codex-gold-ink)]" : "text-muted-foreground"}`}>Sign in</button>
            <button onClick={() => setMode("register")} className={`rounded-full py-2 text-sm font-medium transition-colors ${mode === "register" ? "bg-gold-light text-[color:var(--codex-gold-ink)]" : "text-muted-foreground"}`}>Create account</button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" ? (
              <Field icon={User} type="text" placeholder="Your name" value={name} onChange={setName} testid="auth-name-input" required />
            ) : null}
            <Field icon={Mail} type="email" placeholder="you@example.com" value={email} onChange={setEmail} testid="auth-email-input" required />
            <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={setPassword} testid="auth-password-input" required />
            <button data-testid="auth-submit-button" type="submit" disabled={loading} className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-[color:var(--codex-gold-ink)] disabled:opacity-60" style={{ backgroundColor: "#D8B66A" }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === "login" ? "Enter the archive" : "Begin exploring"}
            </button>
          </form>

          <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground/60"><div className="h-px flex-1 bg-white/10" /> or <div className="h-px flex-1 bg-white/10" /></div>
          <button
            data-testid="auth-google-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.03] py-3 text-sm hover:border-white/25 transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <p className="mt-5 text-center text-[12px] leading-5 text-muted-foreground/70">
            A place to think, not to be told what to believe. Your notes and collections stay private to you.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, type, placeholder, value, onChange, testid, required }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <input data-testid={testid} type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="h-12 w-full bg-transparent text-sm focus:outline-none" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.9 1.5l2.6-2.5C17 .9 14.7 0 12 0 5.4 0 0 5.4 0 12s5.4 12 12 12c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z"/></svg>
  );
}
